import { Injectable } from '@angular/core';
import { AppService, RocosSdkClientService } from '@shared/services';
import type { MapContent, MapService } from '@dronedeploy/rocos-js-sdk';
import type { Observable, Subscription } from 'rxjs';
import { firstValueFrom, from, timer } from 'rxjs';
import { Subject, lastValueFrom, throwError } from 'rxjs';
import { distinctUntilChanged, filter, finalize, first, map, switchMap, takeUntil, timeout } from 'rxjs/operators';
import type { FeatureCollection } from 'geojson';
import { isEqual } from 'lodash';
import { environment } from '@env/environment';
import type { EnvironmentConfig } from '@shared/webcomponents/webcomponent-widgets/omniviewer.component';

export interface AgentMap {
  cloud: boolean;
  edges: number;
  nodes: number;
  id: string;
  hash: string;
  ready: boolean;
  timestamp: number;
}

export interface RobotMap {
  id: string;
  hash?: string;
  description?: string;
  nodes: number;
  edges: number;
  sizeBytes?: number;
  isDeployed: boolean;
  deployedCount?: number;
  isInCloud: boolean;
  isAgentReady?: boolean;
  isUploading?: boolean;
  deploymentCount?: number;
  lastModifiedTimestamp?: number;
}

@Injectable({
  providedIn: 'root',
})
export class RobotMapsService {
  private mapService: MapService;

  constructor(private rocosSDKClient: RocosSdkClientService, private appService: AppService) {
    this.mapService = this.rocosSDKClient.client.getMapService();
  }

  public getCloudMaps$(refreshWhenUploading = false, includeSystem = false): Observable<RobotMap[]> {
    const allMapsUploaded = new Subject<void>();
    return timer(0, 5000).pipe(
      takeUntil(allMapsUploaded),
      switchMap(() => this.mapService.list(this.appService.projectId, this.appService.callsign, includeSystem)),
      map((cloudMaps) => {
        // if no maps are uploading, stop polling
        if (!refreshWhenUploading || !cloudMaps.some((cloudMap) => cloudMap.isUploading)) {
          allMapsUploaded.next();
          allMapsUploaded.complete();
        }
        return cloudMaps.map((cloudMap) => ({
          id: cloudMap.id,
          hash: cloudMap.hash,
          deployedCount: cloudMap.deployedCount,
          isDeployed: cloudMap.deployed,
          isUploading: cloudMap.isUploading,
          isInCloud: cloudMap.isInCloud,
          edges: cloudMap.edges,
          nodes: cloudMap.nodes,
          sizeBytes: cloudMap.sizeBytes,
          description: cloudMap.description,
          lastModifiedTimestamp: cloudMap.lastModifiedTimestamp,
        }));
      }),
    );
  }

  public getAgentMaps$(): Observable<RobotMap[]> {
    return this.rocosSDKClient.client
      .getTelemetryService()
      .subscribe<Record<string, AgentMap>>({
        projectId: this.appService.projectId,
        callsigns: [this.appService.callsign],
        sources: ['/map/staticMaps?int=1s'],
      })
      .pipe(
        map((res) => res.payload),
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
        map((res) =>
          Object.entries(res).map(([_, agentMap]) => ({
            id: agentMap.id,
            hash: agentMap.hash,
            isDeployed: agentMap.cloud,
            isInCloud: agentMap.cloud,
            edges: agentMap.edges,
            nodes: agentMap.nodes,
            isAgentReady: agentMap.ready,
            lastModifiedTimestamp: agentMap.timestamp,
          })),
        ),
      );
  }

  /** Returns a list of maps that are either in the cloud or on the agent
   *
   * The cloud maps are only queried once. Use getCloudMaps() to get the latest cloud maps.
   *
   * @see getCloudMaps()
   * @see getAgentMaps$()
   */
  public getCombinedMaps$(includeSystem: boolean): Observable<RobotMap[]> {
    const out = new Subject<RobotMap[]>();

    let cloudMaps: RobotMap[] = [];
    let agentMaps: RobotMap[] = [];

    this.getCloudMaps$(false, includeSystem).subscribe({
      next: (res) => {
        cloudMaps = res;
        out.next(this.mergeMapLists(cloudMaps, agentMaps));

        if (!this.appService.callsign) {
          out.complete();
        }
      },
      error: (err) => {
        out.error(err);
      },
    });

    let staticMapsSub: Subscription | undefined;
    if (this.appService.callsign) {
      staticMapsSub = this.getAgentMaps$().subscribe({
        next: (res) => {
          agentMaps = res;
          out.next(this.mergeMapLists(cloudMaps, res));
        },
        error: (err) => {
          out.error(err);
        },
      });
    }

    return out.pipe(
      finalize(() => {
        if (staticMapsSub) staticMapsSub.unsubscribe();
      }),
    );
  }

  public async saveToAgent(dynamicMapId: string, saveAsId: string): Promise<{ hash: string }> {
    const caller = this.rocosSDKClient.client.getCallerService().call<{ hash: string }>({
      callsign: this.appService.callsign,
      projectId: this.appService.projectId,
      source: '/map/staticMaps/save',
      payload: {
        dynamicMapId,
        saveAsId,
      },
    });

    return await lastValueFrom(caller.return$);
  }

  /** One-shot snapshot of dynamic maps with a 10s timeout; suited for dialogs that need the current state once. */
  public getAdapters$(): Observable<Record<string, { nodes: number; edges: number }>> {
    return this.dynamicMapsSource$().pipe(timeout(10000), first());
  }

  /** Continuous stream of dynamic maps; suited for components that need to stay updated as the robot's maps change. */
  public getDynamicMaps$(): Observable<Record<string, { nodes: number; edges: number }>> {
    return this.dynamicMapsSource$().pipe(distinctUntilChanged((prev, curr) => isEqual(prev, curr)));
  }

  public async deleteMapFromRobot(mapId: string, hash: string) {
    return this.mapService.delete(this.appService.projectId, mapId, this.appService.callsign, hash);
  }
  public async deleteMap(mapId: string) {
    return this.mapService.delete(this.appService.projectId, mapId);
  }

  public async updateMap(mapId: string, payload: { description: string }) {
    return this.mapService.updateMetadata(this.appService.projectId, mapId, payload);
  }

  /**
   * Duplicate an existing cloud map. If `name` is omitted, the server picks
   * the next available `<sourceMapId>-copy[-N]` slot; the resolved mapId is
   * returned so the caller can navigate to or refresh the new map.
   */
  public async duplicateMap(
    sourceMapId: string,
    payload: { name?: string; description?: string } = {},
  ): Promise<{ mapId: string }> {
    return this.mapService.copy(this.appService.projectId, sourceMapId, payload);
  }

  /**
   * Create a new cloud map. The server requires at least one node in the
   * content, so the caller must supply a `rootFrameId` — a FRAME node with
   * that id is seeded as the transform root. When `description` is supplied,
   * the server seeds it into the map's SUMMARY in the same write as the
   * create, so the operation is atomic — either both land or neither does.
   *
   * @param mapId        New map id, must be unique in the project.
   * @param rootFrameId  Node id of the seeded FRAME (e.g. "seed", "world").
   * @param description  Optional description for the maps list.
   */
  public async createMap(mapId: string, rootFrameId: string, description?: string): Promise<void> {
    const content: MapContent = {
      nodes: {
        [rootFrameId]: { id: rootFrameId, type: 'FRAME', edges: {}, data: {} },
      },
      edges: {},
    };
    await this.mapService.create(this.appService.projectId, mapId, content, { description });
  }

  public getGeoJSON(mapId: string, frameId?: string): Promise<FeatureCollection> {
    return this.mapService.getGeoJSON(this.appService.projectId, mapId, frameId) as Promise<FeatureCollection>;
  }

  /**
   * Uploads a map to the cloud
   *
   * @returns observable which emits true when the map is ready in the cloud
   */
  public async uploadMapToCloud(id: string, hash: string): Promise<void> {
    return this.mapService.sendToCloud(this.appService.projectId, id, this.appService.callsign, hash);
  }

  /** Merges the cloud and agent maps into a single list
   *
   * If a map exists in both lists, the isDeployed flag is set to true
   *
   * @param cloudMaps list of maps from the cloud
   * @param agentMaps list of maps from the agent
   * @returns merged list of maps
   */
  public mergeMapLists<T extends RobotMap>(cloudMaps: T[], agentMaps: T[]): T[] {
    const maps: Array<T> = [];

    for (const robotMap of cloudMaps) {
      maps.push(robotMap);
    }

    for (const agentMap of agentMaps) {
      const idx = maps.findIndex((m) => m.hash === agentMap.hash);
      const cloudMap = maps[idx];
      if (cloudMap) {
        if (!cloudMap.isInCloud) {
          // newly saved maps before they have been uploaded to the cloud
          maps[idx].edges = agentMap.edges;
          maps[idx].nodes = agentMap.nodes;
          maps[idx].hash = agentMap.hash;
        }

        maps[idx].isAgentReady = agentMap.isAgentReady;
      } else {
        maps.unshift(agentMap);
      }
    }

    return maps;
  }

  public async loadMap(mapId: string, adaptor: string, hash: string) {
    await lastValueFrom(
      this.rocosSDKClient.client.getCallerService().call({
        projectId: this.appService.projectId,
        callsign: this.appService.callsign,
        source: '/map/staticMaps/load',
        payload: {
          dynamicMapId: adaptor,
          sourceId: mapId,
          sourceHash: hash,
        },
      }).return$,
    );
  }

  public async callServiceRaw(projectId: string, callsign: string, source: string, payload: unknown = {}) {
    const options = {
      query: {
        enc: ['raw'], // Specify raw encoding in the service query.
      },
    };
    const { return$ } = this.rocosSDKClient.client
      .getCallerService()
      .callRaw({ projectId, callsign, source, payload, options });
    const response = await firstValueFrom(return$);
    // For raw calls, we need to decode the payload manually
    if (response?.payload) {
      return new TextDecoder().decode(response.payload);
    }
    return response;
  }

  public callServiceStream(projectId: string, callsign: string, source: string, payload: unknown) {
    const { return$ } = this.rocosSDKClient.client.getCallerService().call({ projectId, callsign, source, payload });
    return return$;
  }

  public async deployMap(mapId: string) {
    return this.rocosSDKClient.client
      .getMapService()
      .deploy(this.appService.projectId, mapId, this.appService.callsign);
  }

  public getEnvironmentConfig$(): Observable<EnvironmentConfig> {
    return from(this.rocosSDKClient.client.getProjectService().info(this.appService.projectId)).pipe(
      filter(Boolean),
      map(
        (info): EnvironmentConfig => ({
          mapBoxToken: environment.mapbox.accessToken,
          googleMapsApiKey: environment.google.mapsApiKey,
          tilesUrl: environment.droneDeploy?.tilesApi,
          origin: { lat: info.location.lat, lon: info.location.lng, alt: 0 },
        }),
      ),
    );
  }

  private dynamicMapsSource$(): Observable<Record<string, { nodes: number; edges: number }>> {
    if (!this.appService.callsign) {
      return throwError(() => new Error('No callsign selected'));
    }

    return this.rocosSDKClient.client
      .getTelemetryService()
      .subscribe<Record<string, { nodes: number; edges: number }>>({
        projectId: this.appService.projectId,
        callsigns: [this.appService.callsign],
        sources: ['/map/dynamicMaps'],
      })
      .pipe(map((res) => res.payload));
  }
}
